import { Checkbox, ColorPicker, Input, Select, Text } from '@viapeople/components'
import { useState } from 'react'
import { DEFAULT_COLORS } from '../constants'

/**
 * @fileoverview PEC Rating Scale Editor
 *
 * Component for configuring custom rating scales in PEC cycles.
 * Allows setting the number of rating options, custom labels and colors,
 * and enabling N/A option.
 */

interface PecRatingOption {
	value: string
	valueLabel: string
	labelColor: string
}

interface PecRatingScaleEditorProps {
	count: number
	options: PecRatingOption[]
	allowNa: boolean
	onChange: (config: { count: number; options: PecRatingOption[]; allowNa: boolean }) => void
	disabled?: boolean
}

/**
 * Generate default rating options for a given scale
 */
function generateDefaultOptions(count: number): PecRatingOption[] {
	return Array.from({ length: count }, (_, i) => ({
		value: String(i + 1),
		valueLabel: String(i + 1),
		labelColor: DEFAULT_COLORS[i] || '#2563EB',
	}))
}

export const PecRatingScaleEditor = ({
	count,
	options,
	allowNa,
	onChange,
	disabled = false,
}: PecRatingScaleEditorProps) => {
	const [selectedRatingIndex, setSelectedRatingIndex] = useState(0)

	// Ensure options array matches count
	const normalizedOptions: PecRatingOption[] = Array.from({ length: count }, (_, i) => {
		if (options[i]) {
			return options[i]
		}
		return {
			value: String(i + 1),
			valueLabel: String(i + 1),
			labelColor: DEFAULT_COLORS[i] || '#2563EB',
		}
	})

	const handleCountChange = (newCount: number) => {
		const clampedCount = Math.max(2, Math.min(10, newCount))
		const newOptions = generateDefaultOptions(clampedCount)

		// Preserve existing options where possible
		for (let i = 0; i < Math.min(normalizedOptions.length, clampedCount); i++) {
			newOptions[i] = normalizedOptions[i]
		}

		// Reset selected index if out of bounds
		if (selectedRatingIndex >= clampedCount) {
			setSelectedRatingIndex(0)
		}

		onChange({
			count: clampedCount,
			options: newOptions,
			allowNa,
		})
	}

	const handleOptionChange = (index: number, field: keyof PecRatingOption, value: string) => {
		const newOptions = [...normalizedOptions]
		newOptions[index] = {
			...newOptions[index],
			[field]: value,
		}
		onChange({
			count,
			options: newOptions,
			allowNa,
		})
	}

	const handleAllowNaChange = (checked: boolean) => {
		onChange({
			count,
			options: normalizedOptions,
			allowNa: checked,
		})
	}

	const ratingSelectOptions = Array.from({ length: count }, (_, i) => ({
		value: String(i),
		label: `Rating ${i + 1}`,
	}))

	const selectedOption = normalizedOptions[selectedRatingIndex] || {
		value: String(selectedRatingIndex + 1),
		valueLabel: String(selectedRatingIndex + 1),
		labelColor: '#1E293B',
	}

	return (
		<div className='space-y-6'>
			{/* Scale Count */}
			<div className='space-y-4'>
				<Text variant='small' weight='semibold'>
					Rating Scale
				</Text>
				<Text variant='xsmall' className='text-neutral-600'>
					Configure the number of rating options and customize each value.
				</Text>

				<div className='flex items-center gap-4'>
					<div className='w-32'>
						<Input
							label='Scale Count'
							type='number'
							min={2}
							max={10}
							value={count}
							onChange={(e) => handleCountChange(Number(e.target.value))}
							disabled={disabled}
						/>
					</div>
					<Text variant='xsmall' className='text-neutral-500 pt-6'>
						(2-10 options)
					</Text>
				</div>
			</div>

			{/* Rating Options Editor */}
			<div className='space-y-4'>
				<Text variant='small' weight='semibold'>
					Rating Options
				</Text>

				<div className='space-y-3'>
					<Select
						label='Select Rating to Edit'
						value={String(selectedRatingIndex)}
						onChange={(val) => setSelectedRatingIndex(Number(val))}
						options={ratingSelectOptions}
						placeholder='Select a rating to edit'
						disabled={disabled}
					/>

					<div className='space-y-3 p-4 border border-neutral-200 rounded-lg bg-neutral-50'>
						<div>
							<Input
								label='Rating Value'
								value={selectedOption.value}
								onChange={(e) => handleOptionChange(selectedRatingIndex, 'value', e.target.value)}
								placeholder='Enter value'
								maxLength={10}
								disabled={disabled}
							/>
							<Text variant='xsmall' className='text-neutral-500 mt-1'>
								The value stored when this rating is selected
							</Text>
						</div>

						<div>
							<Input
								label='Display Label'
								value={selectedOption.valueLabel}
								onChange={(e) => handleOptionChange(selectedRatingIndex, 'valueLabel', e.target.value)}
								placeholder='Enter label'
								disabled={disabled}
							/>
							<Text variant='xsmall' className='text-neutral-500 mt-1'>
								The label shown to participants (e.g., "Poor", "Fair", "Good")
							</Text>
						</div>

						<div>
							<Text variant='small' className='block mb-2'>
								Label Color
							</Text>
							<ColorPicker
								value={selectedOption.labelColor || '#1E293B'}
								onChange={(val) => handleOptionChange(selectedRatingIndex, 'labelColor', val)}
								placeholder='#1E293B'
							/>
						</div>

						{/* Preview */}
						<div className='mt-4 p-3 rounded border border-neutral-200 bg-white'>
							<Text variant='xsmall' className='text-neutral-500 mb-2'>
								Preview
							</Text>
							<div
								className='inline-flex items-center justify-center px-4 py-2 rounded-lg font-semibold'
								style={{
									backgroundColor: selectedOption.labelColor,
									color: 'white',
								}}>
								{selectedOption.value}
							</div>
							<Text variant='small' weight='semibold' className='ml-3' style={{ color: selectedOption.labelColor }}>
								{selectedOption.valueLabel}
							</Text>
						</div>
					</div>
				</div>
			</div>

			{/* N/A Option */}
			<div className='space-y-4 p-4 bg-neutral-50 rounded-lg border border-neutral-200'>
				<div className='flex items-start gap-3'>
					<Checkbox
						id='allowNa'
						checked={allowNa}
						onChange={(e) => handleAllowNaChange(e.target.checked)}
						disabled={disabled}
					/>
					<div className='flex-1'>
						<label htmlFor='allowNa'>
							<Text variant='small' weight='semibold'>
								Allow N/A Option
							</Text>
							<Text variant='xsmall' className='text-neutral-600 mt-1'>
								When enabled, participants can select "N/A" (Not Applicable) if they cannot provide a rating for
								someone. N/A responses are excluded from average calculations.
							</Text>
						</label>
					</div>
				</div>
			</div>

			{/* Full Scale Preview */}
			<div className='space-y-2'>
				<Text variant='small' weight='semibold'>
					Full Scale Preview
				</Text>
				<div className='p-4 bg-neutral-50 border border-neutral-200 rounded-lg'>
					<div className='flex gap-2 flex-wrap'>
						{normalizedOptions.map((option, index) => (
							<div key={`${option.value}-${option.valueLabel}`} className='text-center'>
								<button
									type='button'
									className='w-12 h-12 flex items-center justify-center rounded-lg font-semibold text-sm cursor-pointer transition-colors border-2'
									style={{
										backgroundColor: index === selectedRatingIndex ? option.labelColor : 'white',
										color: index === selectedRatingIndex ? 'white' : '#374151',
										borderColor: index === selectedRatingIndex ? option.labelColor : '#E5E7EB',
									}}
									onClick={() => setSelectedRatingIndex(index)}>
									{option.value}
								</button>
								<Text
									variant='xsmall'
									className='mt-1 max-w-[60px] truncate'
									style={{ color: option.labelColor || '#1E293B' }}>
									{option.valueLabel}
								</Text>
							</div>
						))}
						{allowNa && (
							<div className='text-center'>
								<div className='w-12 h-12 flex items-center justify-center rounded-lg font-semibold text-sm bg-neutral-200 text-neutral-600 border-2 border-neutral-300'>
									N/A
								</div>
								{/* Empty placeholder to maintain alignment with other options that have labels */}
								<div className='mt-1 h-4' />
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
